/**
 * The Word document's save step (contract 1.12.0): the layout control, and the
 * text a preview pane shows for the written file.
 *
 * THE LAYOUT is `DocxFormatOptions.layout`, a table with times or one plain
 * paragraph per subtitle. It is a write option, as the text encoding is, so it
 * works the same way (lib/save-encoding.ts): the words are the template's
 * (`ui.docx_layout` in macros/ui.html), a page mounts it once and calls `sync`
 * wherever its write format changes, and every write asks it for the
 * `formatOptions` to send. A page without the field writes the default table.
 *
 * THE PREVIEW. The format step shows the written file's head, which for a
 * text format is the file itself. A DOCX is a ZIP, and its bytes decoded as
 * text are a header and XML. `docxText` reads the document part back out and
 * gives the words in the order a reader sees them: one line per table row, or
 * one per paragraph. The writer stores its parts uncompressed
 * (`src/formats/write/docx.ts`), so this is a walk over local headers with no
 * inflate; anything else returns null and the caller shows nothing.
 */

import type { DocxFormatOptions, WriteOptions } from '../../../../../contracts/cue-model.ts';
import { on, qs, qsa } from './dom.ts';

export type DocxLayout = DocxFormatOptions['layout'];

/** Anything but the exact value 'paragraphs' is the table, the writer's default. */
export function layoutOf(value: string | null | undefined): DocxLayout {
  return value === 'paragraphs' ? 'paragraphs' : 'table';
}

/** The `formatOptions` a write in `format` sends: the layout for DOCX, nothing for the rest. */
export function docxFormatOptions(
  format: string | undefined,
  layout: DocxLayout,
): WriteOptions['formatOptions'] {
  return format === 'docx' ? { format: 'docx', layout } : null;
}

export interface DocxLayoutControl {
  /** Show the field while `format` is DOCX, hide it otherwise. */
  sync(format: string | undefined): void;
  /** The `formatOptions` a write in `format` uses right now. */
  formatOptionsFor(format: string | undefined): WriteOptions['formatOptions'];
}

/**
 * Mount the field under `root`. `onChange` runs when the user picks the other
 * layout, and should write the file again so the button holds that layout.
 */
export function mountDocxLayout(root: ParentNode, onChange: () => void): DocxLayoutControl {
  const field = qs<HTMLElement>('[data-docx-layout-field]', root);
  const radios = field ? qsa<HTMLInputElement>('[data-docx-layout]', field) : [];
  for (const radio of radios) {
    on(radio, 'change', () => {
      if (radio.checked) onChange();
    });
  }
  const picked = (): DocxLayout => layoutOf(radios.find((r) => r.checked)?.value);
  return {
    sync(format) {
      if (field) field.hidden = format !== 'docx';
    },
    formatOptionsFor: (format) => docxFormatOptions(format, picked()),
  };
}

/*───────────────────────────────────────────────────────────────────────────
  The preview
───────────────────────────────────────────────────────────────────────────*/

const LOCAL_HEADER = 0x04034b50;
const DOCUMENT_PART = 'word/document.xml';

/** `&amp;` and its four siblings, the only references the writer emits. */
function unescapeXml(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

/** The words in a fragment of WordprocessingML: text runs, breaks as newlines, tabs as tabs. */
function wordsOf(fragment: string): string {
  return unescapeXml(
    fragment
      .replace(/<w:br\/>/g, '\n')
      .replace(/<w:tab\/>/g, '\t')
      .replace(/<[^>]+>/g, ''),
  );
}

/** The text of `word/document.xml`: a table row per line, cells two spaces apart, or paragraphs. */
export function documentText(xml: string): string {
  const rows = [...xml.matchAll(/<w:tr>([\s\S]*?)<\/w:tr>/g)];
  if (rows.length > 0) {
    return rows
      .map((row) =>
        [...(row[1] ?? '').matchAll(/<w:tc>([\s\S]*?)<\/w:tc>/g)]
          .map((cell) => wordsOf(cell[1] ?? ''))
          .join('  '),
      )
      .join('\n');
  }
  return [...xml.matchAll(/<w:p(?:\s[^>]*)?>([\s\S]*?)<\/w:p>/g)]
    .map((p) => wordsOf(p[1] ?? ''))
    .join('\n\n');
}

/** The text of a DOCX this site wrote, or null for bytes it cannot read that way. */
export function docxText(bytes: Uint8Array): string | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const utf8 = new TextDecoder('utf-8');
  let at = 0;
  while (at + 30 <= bytes.length && view.getUint32(at, true) === LOCAL_HEADER) {
    const method = view.getUint16(at + 8, true);
    const size = view.getUint32(at + 18, true);
    const nameLength = view.getUint16(at + 26, true);
    const extraLength = view.getUint16(at + 28, true);
    const name = utf8.decode(bytes.subarray(at + 30, at + 30 + nameLength));
    const start = at + 30 + nameLength + extraLength;
    if (start + size > bytes.length) return null;
    if (name === DOCUMENT_PART) {
      return method === 0 ? documentText(utf8.decode(bytes.subarray(start, start + size))) : null;
    }
    at = start + size;
  }
  return null;
}
